import type { ItemId } from '@/data/items';
import type { Season } from '@/time/clock';

/**
 * The people.
 *
 * Content, not code: who exists, where they are at each hour, and what they
 * say. Adding a villager is an entry here and a sprite, not a change to how
 * anything works.
 *
 * A schedule is a list of postings ordered by hour, and whoever is posted
 * latest before the current hour is where they are. That is simpler than
 * intervals and cannot leave a gap — there is always a most-recent posting,
 * so an NPC is never nowhere.
 */

export type NpcId = 'pell' | 'mira';

export interface Posting {
  /** Hour this posting begins. The last one at or before now wins. */
  readonly fromHour: number;
  /** Null means off-stage: at home, asleep, not in the world. */
  readonly area: string | null;
  readonly x?: number;
  readonly y?: number;
}

export interface DialogueLine {
  readonly text: string;
  readonly when?: {
    readonly hourFrom?: number;
    readonly hourTo?: number;
    readonly season?: Season;
    readonly raining?: boolean;
    /**
     * Only on this festival.
     *
     * A person standing at a market talking about their ordinary day is the
     * clearest possible sign that the festival is a costume over the usual
     * schedule, which it is — so the one thing that must not be ordinary is
     * what they say.
     */
    readonly festival?: string;
  };
}

export interface NpcDefinition {
  readonly id: NpcId;
  readonly name: string;
  readonly schedule: readonly Posting[];
  /** First matching line wins, so put the specific ones first. */
  readonly lines: readonly DialogueLine[];
  /** True if talking to them can open the cart. */
  readonly trades: boolean;
  /** Items that land especially well, and especially badly. */
  readonly likes: readonly ItemId[];
  readonly dislikes: readonly ItemId[];
  /** What they say when given something they love, or cannot abide. */
  readonly onLiked: string;
  readonly onNeutral: string;
  readonly onDisliked: string;
}

export const NPCS: Readonly<Record<NpcId, NpcDefinition>> = {
  pell: {
    id: 'pell',
    name: 'Pell',
    trades: true,
    // A merchant values what sells. Blight sap is what he spends his life
    // driving around, and he does not want it in his hands.
    likes: ['mat:pearl', 'crop:pumpkin', 'fish:goldenCarp'],
    dislikes: ['mat:blightSap'],
    onLiked: 'Now that — that I can shift. You have a good eye.',
    onNeutral: 'Kind of you. It will find a buyer somewhere.',
    onDisliked: 'Keep it. I have carted enough of that to last me.',
    // Sets up mid-morning, trades through the afternoon, packs up at dusk.
    // The gaps are the point: a merchant who is always there is a vending
    // machine, and the cart being shuttered at night is what makes him a man
    // with somewhere else to be.
    schedule: [
      { fromHour: 0, area: null },
      { fromHour: 8, area: 'farm', x: 24, y: 16 },
      { fromHour: 18, area: null },
    ],
    lines: [
      {
        text: 'Half the county on my doorstep and not one of them buying seed. Bring me a crop.',
        when: { festival: 'blossomMarket' },
      },
      {
        text: 'Sit, eat, and do not tell me what the cart looks like from the outside tonight.',
        when: { festival: 'harvestSupper' },
      },
      {
        text: 'I came for the shade and there is none. Mira is judging fish down there.',
        when: { festival: 'longestDay' },
      },
      {
        text: 'Every lantern on that water was bought from me. Every one. Remember that.',
        when: { festival: 'lanternNight' },
      },
      {
        text: 'Rain like this and the wheels sink to the axle. I am not going anywhere.',
        when: { raining: true },
      },
      {
        text: 'Morning. Give me a moment, half the crates are still roped down.',
        when: { hourFrom: 8, hourTo: 10 },
      },
      {
        text: 'Winter trade is thin. Everyone wants lamp oil and nobody wants seeds.',
        when: { season: 'winter' },
      },
      {
        text: 'Light is going. I will be packed and gone before dark.',
        when: { hourFrom: 16 },
      },
      { text: 'Anything you have grown, I will take. Anything I have, you can have for coin.' },
    ],
  },
  // The smith downriver Pell keeps mentioning is her father. She works the
  // shore because that is where the driftwood comes ashore, which is also why
  // her hours are the tide's rather than a shop's.
  mira: {
    id: 'mira',
    name: 'Mira',
    trades: false,
    // A smith values what goes into a blade. A feather does not.
    likes: ['mat:mothDust', 'mat:blightSap', 'crop:tomato'],
    dislikes: ['mat:feather'],
    onLiked: 'Oh — I can *use* this. Give me a week and you will not know it.',
    onNeutral: 'Thank you. I will find a corner for it.',
    onDisliked: 'A feather. For a forge. You are teasing me.',
    schedule: [
      { fromHour: 0, area: null },
      { fromHour: 9, area: 'coast', x: 14, y: 24 },
      { fromHour: 19, area: null },
    ],
    lines: [
      {
        text: 'Longest day of the year and I have not touched the forge once. Show me a fish.',
        when: { festival: 'longestDay' },
      },
      {
        text: 'We float them out on the tide. Bring something that shines and I will show you.',
        when: { festival: 'lanternNight' },
      },
      {
        text: 'The market comes to the farm and I come with it. Father is minding the fire.',
        when: { festival: 'blossomMarket' },
      },
      {
        text: 'I have eaten three helpings and I intend to keep going. Do not judge me.',
        when: { festival: 'harvestSupper' },
      },
      {
        text: 'Rain on hot iron. Stand back unless you fancy a scar to explain.',
        when: { raining: true },
      },
      {
        text: 'Fire is barely awake. Neither am I, if we are being honest.',
        when: { hourFrom: 9, hourTo: 11 },
      },
      {
        text: 'Sea coal burns wet in winter. Everything takes twice as long.',
        when: { season: 'winter' },
      },
      {
        text: 'Nearly done. Father wants the barrow back before dark.',
        when: { hourFrom: 17 },
      },
      { text: 'Driftwood, salt and patience. That is the whole trade, mostly.' },
    ],
  },
};

export const NPC_IDS = Object.keys(NPCS) as NpcId[];

/** Where someone is at a given hour, or null when they are off-stage. */
export function postingAt(npc: NpcDefinition, hour: number): Posting | null {
  let current: Posting | null = null;
  for (const posting of npc.schedule) {
    if (posting.fromHour <= hour) current = posting;
  }
  return current && current.area !== null ? current : null;
}
